import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, LogOut, ChevronRight, Bell, Lock, Info, BellRing, BellOff, Calendar, AlertTriangle, CheckCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { pushService } from '../services/pushNotifications';
import MobileLayout from '../components/layout/MobileLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useToast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new_pwd: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, []);

  useEffect(() => {
    // Check current notification permission
    const permission = pushService.getPermissionStatus();
    setNotificationPermission(permission);
    setNotificationsEnabled(permission === 'granted');
    
    // Load notifications
    loadNotifications();
  }, [loadNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'shift':
      case 'shift_reminder':
        return <Calendar className="w-4 h-4 text-blue-600" />;
      case 'incident':
        return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      default:
        return <Bell className="w-4 h-4 text-slate-600" />;
    }
  };

  useEffect(() => {
    // Check current notification permission
    const permission = pushService.getPermissionStatus();
    setNotificationPermission(permission);
    setNotificationsEnabled(permission === 'granted');
  }, []);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/login');
    }
  };

  const handleToggleNotifications = async () => {
    if (notificationsEnabled) {
      // Can't revoke notification permission programmatically
      toast({
        title: "Notifications",
        description: "To disable notifications, please use your browser settings.",
      });
      return;
    }

    const granted = await pushService.requestPermission();
    if (granted) {
      setNotificationsEnabled(true);
      setNotificationPermission('granted');
      toast({
        title: "Notifications Enabled!",
        description: "You'll receive alerts for incidents, shifts, and more.",
      });
      
      // Show a test notification
      setTimeout(() => {
        pushService.showNotification('Notifications Active', {
          body: 'You will now receive important alerts from TheSecurityGuardApp',
        });
      }, 1000);
    } else {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "Please allow notifications in your browser settings.",
      });
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      guard: 'Security Guard',
      supervisor: 'Supervisor',
      admin: 'Admin',
      super_admin: 'Super Admin',
    };
    return labels[role] || role;
  };

  return (
    <MobileLayout>
      <div className="p-4 space-y-6" data-testid="profile-page">
        <h1 className="text-2xl font-bold text-slate-800">Profile</h1>

        {/* User Info Card */}
        <Card>
          <CardContent className="p-6 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-blue-700">
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <h2 className="text-xl font-bold text-slate-800">{user?.name}</h2>
            <p className="text-blue-700 font-medium">{getRoleLabel(user?.role)}</p>
            
            <div className="mt-4 space-y-2 text-left">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-600">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">{user?.phone}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {notificationsEnabled ? (
                  <BellRing className="w-5 h-5 text-green-600" />
                ) : (
                  <BellOff className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-800">Push Notifications</p>
                  <p className="text-xs text-slate-500">
                    {notificationsEnabled 
                      ? 'Receiving alerts for incidents & shifts' 
                      : 'Enable to receive important alerts'}
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleToggleNotifications}
              />
            </div>
            
            {notificationPermission === 'denied' && (
              <p className="text-xs text-red-500 mt-2">
                Notifications blocked. Please enable in browser settings.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        {notifications.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-500 uppercase">Recent Notifications</h3>
              <Badge variant="outline" className="text-xs">
                {notifications.filter(n => !n.read).length} unread
              </Badge>
            </div>
            <Card>
              <CardContent className="p-0 divide-y divide-slate-100">
                {(showAllNotifications ? notifications : notifications.slice(0, 5)).map((notification) => (
                  <div 
                    key={notification.id}
                    className={`p-3 flex items-start gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => markAsRead(notification.id)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!notification.read ? 'bg-blue-100' : 'bg-slate-100'}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {notification.created_at ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {notifications.length > 5 && (
                  <button 
                    onClick={() => setShowAllNotifications(!showAllNotifications)}
                    className="w-full p-3 text-sm text-blue-600 hover:bg-slate-50 font-medium"
                  >
                    {showAllNotifications ? 'Show less' : `Show all ${notifications.length} notifications`}
                  </button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings */}
        <div>
          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Settings</h3>
          <Card>
            <CardContent className="p-0 divide-y divide-slate-100">
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="change-password-btn"
              >
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-700">Change Password</span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </button>
              <button className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-700">About</span>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Logout */}
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log Out
        </Button>

        {/* App Version */}
        <p className="text-center text-xs text-slate-400">
          TheSecurityGuardApp v2.0 • PWA
        </p>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (passwordForm.new_pwd !== passwordForm.confirm) {
                toast({ title: "Error", description: "New passwords don't match", variant: "destructive" });
                return;
              }
              if (passwordForm.new_pwd.length < 6) {
                toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
                return;
              }
              setChangingPassword(true);
              try {
                await api.changePassword(passwordForm.current, passwordForm.new_pwd);
                toast({ title: "Success", description: "Password changed successfully" });
                setShowChangePassword(false);
                setPasswordForm({ current: '', new_pwd: '', confirm: '' });
              } catch (err) {
                toast({ title: "Error", description: err.message || "Failed to change password", variant: "destructive" });
              } finally {
                setChangingPassword(false);
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrent ? "text" : "password"}
                  placeholder="Enter current password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showNew ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={passwordForm.new_pwd}
                  onChange={(e) => setPasswordForm(p => ({ ...p, new_pwd: e.target.value }))}
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                placeholder="Re-enter new password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={changingPassword} className="w-full bg-blue-600 hover:bg-blue-700">
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
